import { useMemo } from 'react';
import { matchPath, Params, useLocation, useParams } from 'react-router-dom';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { ParsedUniqueId, safeParseUniqueId } from '@/typings/parseUniqueId';
import { UrlEnvironmentType } from '@/typings/url';
import { truthy } from '@/util/array';
import { AccountParams, UrlProjectParams } from '@/util/linkTypes';
import { PositiveInt, validatePositiveIntStr } from '@/util/positiveInt';
import { validateResourceType } from '@/util/resourceType';
import { Prettify } from '@/util/type';

/**
 * For standard resources with integer ids, use a positiveIntParams type (i.e. resourceId)
 * For special case entities with string ids, use resourceKuid and subResourceKuid from StringParams
 */

export const positiveIntParams = [
  'accountId',
  'projectId',
  'environmentId',
  'developId',
] as const;

export const stringParams = [
  'dbtUniqueId',
  'resourceType',
  'sourceName',
  'packageName',
  'databaseName',
  'schemaName',
  'environmentType',
  'assetId',
] as const;

export type PositiveIntParams = (typeof positiveIntParams)[number];
export type StringParams = (typeof stringParams)[number];

type RouteParams = PositiveIntParams | StringParams;

function verifyPositiveInts(routeParams: Params<RouteParams>) {
  const positiveInts: Record<PositiveIntParams, PositiveInt | undefined> = {
    accountId: undefined,
    projectId: undefined,
    environmentId: undefined,
    developId: undefined,
  };
  positiveIntParams.forEach((positiveIntStr) => {
    positiveInts[positiveIntStr] = validatePositiveIntStr(routeParams[positiveIntStr]);
  });
  return positiveInts;
}

type ValidatedParams = Prettify<
  Omit<
    Readonly<Params<RouteParams>>,
    'accountId' | 'projectId' | 'environmentId' | 'developId'
  > & {
    /** @deprecated Prefer `useDbtCloud().accountId` */
    readonly accountId: PositiveInt | undefined;
    /** @deprecated Prefer `useDbtCloud().projectId` */
    readonly projectId: PositiveInt | undefined;
    /** @deprecated Prefer `useDbtCloud().environmentId` */
    readonly environmentId: PositiveInt | undefined;
    readonly developId: PositiveInt | undefined;
    readonly dbtUniqueId: string | undefined;
    readonly resourceType: string | undefined;
    readonly uniqueId: ParsedUniqueId | undefined;
    readonly environmentType: UrlEnvironmentType | undefined;
  }
>;

/**
 * Returns the raw URL matching values, validated and parsed.
 * This would mean it could be missing an environmentId or projectId
 * depending on how Explorer is being accessed, and this method
 * should not be used for accessing these values.
 *
 * 1. In nearly all cases, use `useDbtCloud()` to access the current
 *    project and environment from the URL + Admin API.
 * 2. If needed, use {@link useRawParams} to access the raw URL values.
 */
export function useValidatedParams() {
  const routeParams = useParams<RouteParams>();
  const positiveIntParams = verifyPositiveInts(routeParams);
  const rawUniqueId = routeParams.dbtUniqueId;
  const parsedUniqueId = rawUniqueId ? safeParseUniqueId(rawUniqueId).ok : undefined;
  const environmentType = routeParams.environmentType as UrlEnvironmentType;
  const resourceType =
    parsedUniqueId?.resourceType || validateResourceType(routeParams.resourceType);

  return {
    ...routeParams,
    ...positiveIntParams,
    dbtUniqueId: rawUniqueId,
    uniqueId: parsedUniqueId,
    environmentType: environmentType,
    resourceType,
  } as const satisfies ValidatedParams;
}

/**
 * Routes are expected to look like this: `/1/projects/672/`.
 * This hook will just return the parsed URL values, without
 * use of React Router or the admin API.
 */
export function useRawParams() {
  const pathname = useLocation().pathname;

  return useMemo(() => {
    const pathParts = pathname.split('/');
    if (isNaN(parseInt(pathParts[1]))) {
      // Remove the first element if we expect "/explore/" to be in URL.
      // This is only relevant in cloud-ui where `/explore/` is not the
      // basename of the BrowserRouter.
      pathParts.shift();
    }

    const [
      _empty,
      accountIdStr,
      _projects,
      projectIdStr,
      _environments,
      environmentType,
    ] = [...pathParts, undefined, undefined, undefined];
    return {
      accountId: accountIdStr ? parseInt(accountIdStr) : undefined,
      projectId: projectIdStr ? parseInt(projectIdStr) : undefined,
      // TODO: do some environmentType validation
      environmentType: environmentType as UrlEnvironmentType,
    };
  }, [pathname]);
}

/**
 * If you're not in a MultiProject view, you can access the Project-level URL params.
 *
 * Retrieves the url params, meaning it will only have
 * a accountId, projectId, and environmentType. Prefer `useDbtCloud()` if you want to guarantee
 * the data is present, regardless of the URL.
 */
export const useUrlProjectParamsSafe = (): UrlProjectParams | undefined => {
  /**
   * We are using the deprecated IDs on purpose to return UrlParams, which
   * is based on what we find in the URL rather than what we can fetch from Admin API.
   */

  const { accountId, projectId } = useDbtCloudProject();
  // If we have no environmentType in the URL, assume 'production'
  const environmentType = useRawParams().environmentType;

  return useMemo(() => {
    if (!accountId || !projectId || !environmentType) return undefined;
    return {
      accountId,
      projectId,
      environmentType,
    };
  }, [accountId, projectId, environmentType]);
};

export type ProjectScope = UrlProjectParams & AccountParams;

/**
 * If you're not in a MultiProject view, you can access the Project-level URL params.
 * @deprecated Use useProjectScope instead
 */
export const useUrlProjectParams = (): ProjectScope => {
  return useProjectScope();
};

/**
 * A unified hook that retrieves project scope either from DbtCloudProjectProvider context
 * or from URL parameters. This hook is designed to work in both scenarios:
 * 1. When inside DbtCloudProjectProvider
 * 2. When accessing project scope from URL parameters
 *
 * @returns ProjectScope containing accountId, projectId, and environmentType
 */
export const useProjectScope = (): ProjectScope => {
  const dbtCloudProject = useDbtCloudProject();
  const urlParams = useUrlProjectParamsSafe();

  if (urlParams) return urlParams;
  if (dbtCloudProject.isInContext && dbtCloudProject.isMounted) {
    return {
      accountId: dbtCloudProject.accountId,
      projectId: dbtCloudProject.projectId,
      environmentType: dbtCloudProject.defaultEnvironmentType || 'production',
    };
  }
  return (
    urlParams || {
      accountId: 0,
      projectId: 0,
      environmentType: 'production',
    }
  );
};

/**
 * Returns the params from all possible matching routes.
 * @param patterns
 */
export const useParamsFromMatchingRoutes = (patterns: string[]) => {
  const location = useLocation();
  const routes = patterns
    .map((pattern) => {
      return matchPath(
        {
          path: pattern,
        },
        location.pathname,
      );
    })
    .filter(truthy);

  if (routes.length === 0) {
    return undefined;
  }

  const params = routes.reduce((acc, route) => {
    return { ...acc, ...route.params };
  }, {} as Params<RouteParams>);

  const parsedUniqueId = params.dbtUniqueId
    ? safeParseUniqueId(params.dbtUniqueId).ok
    : undefined;

  const positiveIntParams = verifyPositiveInts(params);

  return { ...params, ...positiveIntParams, uniqueId: parsedUniqueId };
};
